<template>
  <content-modal
    ref="embedYoutubeModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :primary-button-disabled="false"
    :auto-hide-off="autoHideOff"
    class="embed-youtube-modal"
    @primary-click="embed"
    @modal-hide-request="actionHidden"
  >
    <template #title>{{ t("youtube_add_title") }}</template>
    <template #content>
      <modal-content>
        <cv-form>
          <dl class="field-set type-text url-input">
            <dt class="field-label">{{ t("youtube_url_label") }}</dt>
            <dd class="field-input">
              <text-input
                :invalid-message="error.message"
                :value="youtubeUrl"
                @input="handleUpdateUrl"
              />
            </dd>
          </dl>
        </cv-form>
      </modal-content>
    </template>
    <template #secondary-button>{{ t("button_cancel") }}</template>
    <template #primary-button>
      {{ t("button_add") }}
      <overlay-loader v-if="isLoading" />
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import randomUtils from "@utils/random.utils";
import VueYoutubePlugin from "@plugins/youtube";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import embedUtils from "@plugins/richtext/utils/embed.utils";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import TextInput from "@plugins/common/components/TextInput.vue";
import { useDirty } from "@/plugins/common/composables/page-dirty.ts";

const props = defineProps<{
  params?: { eventBusWatchInstances?: unknown };
}>();

const { t } = useI18n();
const instance = getCurrentInstance();

const id = randomUtils.randomId();
const dirtyId = ref(id);
const dirty = useDirty(dirtyId);

const youtubeUrl = ref("");
const closeAriaLabel = ref("Close");
const size = ref("medium");
const visible = ref(true);
const isLoading = ref(false);
const autoHideOff = ref(true);

const error = ref<{ message: string; subMessage: string }>({ message: "", subMessage: "" });

const hasError = computed(() => error.value.message !== "");

function clearValidationError() {
  error.value.message = "";
  error.value.subMessage = "";
}

function prependProtocol(url: string): string {
  const protocol = "https://";
  return url.includes(protocol) ? url : protocol + url;
}

function validateUrl(): boolean {
  clearValidationError();
  let url = youtubeUrl.value;
  if (url.length === 0) {
    error.value.message = t("ensure_youtube_url_entered_message");
    return false;
  }
  url = prependProtocol(url);
  const isValidUrl = embedUtils.isValidUrl(url);
  const hostName = isValidUrl ? new URL(url).hostname : "";
  const validDomainPattern = ["youtube.com", "youtube-nocookie.com", "youtu.be", "m.youtube.com"];
  const validUrl = isValidUrl && validDomainPattern.some((v) => hostName.includes(v));
  if (validUrl) {
    return true;
  } else {
    error.value.message = t("youtube_key_in_valid_url_message");
    return false;
  }
}

function vnodeKey() {
  return instance?.vnode.key;
}

function embed() {
  if (validateUrl()) {
    isLoading.value = true;
    dirty.removeDirty(id);
    const protocol = "https://";
    if (!youtubeUrl.value.includes(protocol)) {
      youtubeUrl.value = protocol + youtubeUrl.value;
    }
    VueYoutubePlugin.eventBus.$emit("embed-youtube", {
      youtubeUrl: youtubeUrl.value,
    });
    visible.value = false;
    modalEventBus.$emit("modal-hidden", vnodeKey());
    EventBusUtils.unWatchInstance(props.params?.eventBusWatchInstances);
  }
}

async function promptDirty(): Promise<boolean> {
  if (typeof dirty.promptComponentDirty === "function") return dirty.promptComponentDirty();
  if (typeof dirty.promptPageDirty === "function") return dirty.promptPageDirty();
  return true;
}

function actionHidden(event: Record<string, string>) {
  const isExternalClick = event["cv:reason"] === "external-click";
  const isSecondaryClick = event["cv:reason"] === "secondary-click";

  if (!isExternalClick) {
    if (isSecondaryClick) {
      handleActionForSecondaryClick();
    } else {
      handleActionForNonSecondaryClick();
    }
  }
}

function handleActionForSecondaryClick() {
  promptDirty().then((response) => {
    if (response) {
      closeModal();
    }
  });
}

function handleActionForNonSecondaryClick() {
  if (!hasError.value) {
    promptDirty().then((response) => {
      if (response) {
        closeModal();
      }
    });
  }
}

function closeModal() {
  visible.value = false;
  modalEventBus.$emit("modal-hidden", vnodeKey());
  EventBusUtils.unWatchInstance(props.params?.eventBusWatchInstances);
}

function handleUpdateUrl(event: string) {
  youtubeUrl.value = event;
  dirty.setDirty(id);
  clearValidationError();
  if (youtubeUrl.value.length === 0) {
    error.value.message = t("ensure_youtube_url_entered_message");
    return false;
  }
}
</script>
